import { useEffect, useRef, useState } from "react";

import { EmployeeAsyncActions, IAddEmailResponse } from "@/Common/Api/AsyncActions/EmployeeAsyncActions";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { PasswordConfirmationPopup } from "@/Common/Components/Modals/PasswordConfirmationPopup";
import { useHistoricalState } from "@/Common/CustomHooks/useHistoricalState";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ISaveEmailOptions } from "@/Common/Models/Employee/ISaveEmailOptions";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { LoginPage as MyleResourcesForLoginPage } from "@/Common/Resources/StaticResourcesFor";
import { RequiredRecoveryEmailFlowStepEnum } from "@/Common/Workflow/LoginWorkflow/RequiredRecoveryEmailFlowStepEnum";
import { IUseEmailUpdateStateParams, useEmailUpdateState } from "./Hooks/useEmailUpdateState";
import { RecoveryEmailIsMandatoryPopup } from "./RecoveryEmailIsMandatoryPopup";
import { RecoveryEmailPopup } from "./RecoveryEmailPopup";
import { RecoveryEmailSecondRequestPopup } from "./RecoveryEmailSecondRequestPopup";
import { VerifyRecoveryEmailPopup } from "./VerifyRecoveryEmailPopup";

interface IRecoveryEmailControllerProps {
    isForLogin: boolean;
    emailAddressesModel?: IUseEmailUpdateStateParams;
    initialStep?: RequiredRecoveryEmailFlowStepEnum;

    sendSaveEmailRequest: (options: ISaveEmailOptions, password?: string) => Promise<ClientResponse<IAddEmailResponse>>;
    onVerifyEmailSuccess: (verifiedEmail: string) => void;
    onClose?: () => void;
    showCloseButton?: boolean;
}

function RequiredRecoveryEmailController(props: IRecoveryEmailControllerProps): JSX.Element {
    const employeeActions = useRef(new EmployeeAsyncActions());
    const stringProvider = useRef(new ResourceProvider(new MyleResourcesForLoginPage(), sessionStore.locale));
    const emailAddressWithCodeSent = useRef<string | null>(null);
    const triggerVerifyEmailStepOnSavedEmailChange = useRef<boolean>(false);

    const [showPasswordPopup, setShowPasswordPopup] = useState<boolean>(false);

    const { pendingEmail, savedEmail, setPendingEmail, setSavedEmail } = useEmailUpdateState(props.emailAddressesModel);

    const [recoveryEmailFlowStep, previousRecoveryEmailFlowSteps, setRecoveryEmailFlowStep] = useHistoricalState<RequiredRecoveryEmailFlowStepEnum>(
        !savedEmail || savedEmail.isVerified
            ? sessionStore.lastRecoveryEmailPromptDate || !props.isForLogin || cacheStore.ClinicSettings.EnableMandatoryMultifactorAuthentication
                ? RequiredRecoveryEmailFlowStepEnum.MandatoryEmailSecondRequestStep
                : RequiredRecoveryEmailFlowStepEnum.RecoveryEmailStep
            : RequiredRecoveryEmailFlowStepEnum.VerifyRecoveryEmailStep
    );

    useEffect(
        () => {
            if (triggerVerifyEmailStepOnSavedEmailChange.current) {
                setRecoveryEmailFlowStep(RequiredRecoveryEmailFlowStepEnum.VerifyRecoveryEmailStep);
                triggerVerifyEmailStepOnSavedEmailChange.current = false;
            }
        },
        [savedEmail, setRecoveryEmailFlowStep]
    );

    const { getText } = useTranslation();

    async function saveEmail(emailOptions: ISaveEmailOptions, password?: string): Promise<ClientResponse<IAddEmailResponse>> {
        if (emailOptions.emailAddress !== savedEmail?.emailAddress
            || emailOptions.isNotifiedByEmail !== savedEmail?.isNotifiedByEmail
            || emailOptions.isOkToContact !== savedEmail?.isOkToContact
        ) {
            const response = await props.sendSaveEmailRequest(emailOptions, password);

            if (!response.isSuccess)
                return response;

            if (!response.data.isVerified && !IsNullOrWhiteSpace(response.data?.emailAddress)) {
                emailAddressWithCodeSent.current = response.data.emailAddress;// avoid firing userMount() in login flow
                triggerVerifyEmailStepOnSavedEmailChange.current = true;
                setSavedEmail(response.data);
            }
            else {
                props.onClose?.();
            }

            return response;
        }
        else {
            setRecoveryEmailFlowStep(RequiredRecoveryEmailFlowStepEnum.VerifyRecoveryEmailStep);
        }
        return new ClientResponse(null, []);
    }

    async function onSecondRequestNext(): Promise<ClientResponse<IAddEmailResponse>> {
        const isEmailChanged = pendingEmail.emailAddress !== savedEmail?.emailAddress;
        if (props.isForLogin || !isEmailChanged) { // the password is only required for email address change in the Settings tab
            return await saveEmail(pendingEmail);
        }
        else {
            setShowPasswordPopup(true);
            return new ClientResponse(null, []);
        }
    }

    async function sendVerifyEmailRequest(verificationCode: string): Promise<ClientResponse> {
        const response = await employeeActions.current.verifyEmail(
            savedEmail?.emailAddress,
            savedEmail?.contactPointId,
            verificationCode
        );

        if (!response.isSuccess)
            return response;

        props.onVerifyEmailSuccess(savedEmail.emailAddress);

        return response;
    }

    function backToPreviousStep(): void {
        setRecoveryEmailFlowStep(previousRecoveryEmailFlowSteps[0]);
    }

    switch (recoveryEmailFlowStep) {
        case RequiredRecoveryEmailFlowStepEnum.RecoveryEmailStep:
            return (
                <RecoveryEmailPopup
                    emailOptions={pendingEmail}
                    stringProvider={stringProvider.current}
                    onEmailChange={email => setPendingEmail(s => ({ ...s, emailAddress: email }))}
                    onIsOkToContactChanged={isOkToContact => setPendingEmail(s => ({ ...s, isOkToContact }))}
                    onIsNotifiedByEmailChanged={isNotifiedByEmail => setPendingEmail(s => ({ ...s, isNotifiedByEmail }))}
                    onEmailAdded={saveEmail}
                    onContinueWithoutEmail={() => setRecoveryEmailFlowStep(RequiredRecoveryEmailFlowStepEnum.RecoveryEmailIsMandatoryStep)}
                />
            );

        case RequiredRecoveryEmailFlowStepEnum.RecoveryEmailIsMandatoryStep:
            return (
                <RecoveryEmailIsMandatoryPopup
                    stringProvider={stringProvider.current}
                    onBackButtonClick={() => setRecoveryEmailFlowStep(RequiredRecoveryEmailFlowStepEnum.RecoveryEmailStep)}
                    onContinueWithoutEmail={() => {
                        sessionStore.action.updatePromptedForRecoveryEmailAttribute();
                    }}
                />
            );

        case RequiredRecoveryEmailFlowStepEnum.MandatoryEmailSecondRequestStep:
            return (
                <>
                    <RecoveryEmailSecondRequestPopup
                        emailOptions={pendingEmail}
                        stringProvider={stringProvider.current}
                        isWaitingForPassword={showPasswordPopup}
                        onEmailChange={email => setPendingEmail(s => ({ ...s, emailAddress: email }))}
                        onIsOkToContactChanged={isOkToContact => setPendingEmail(s => ({ ...s, isOkToContact }))}
                        onIsNotifiedByEmailChanged={isNotifiedByEmail => setPendingEmail(s => ({ ...s, isNotifiedByEmail }))}
                        onNext={onSecondRequestNext}
                        onClose={props.onClose}
                        showCloseButton={props.showCloseButton}
                    />
                    {showPasswordPopup && (
                        <PasswordConfirmationPopup
                            title={getText("ConfirmPasswordLabel")}
                            clearOnError
                            onCancel={() => setShowPasswordPopup(false)}
                            onConfirm={async password => {
                                const response = await saveEmail(pendingEmail, password);
                                if (response.isSuccess)
                                    setShowPasswordPopup(false);
                                return response;
                            }}
                        />
                    )}
                </>
            );

        case RequiredRecoveryEmailFlowStepEnum.VerifyRecoveryEmailStep:
            if (!savedEmail)
                return null;

            return (
                <VerifyRecoveryEmailPopup
                    email={savedEmail.emailAddress}
                    contactPointId={savedEmail.contactPointId}
                    stringProvider={stringProvider.current}
                    onBack={savedEmail.isVerified
                        ? backToPreviousStep
                        : () => {
                            emailAddressWithCodeSent.current = savedEmail.emailAddress;
                            setRecoveryEmailFlowStep(RequiredRecoveryEmailFlowStepEnum.MandatoryEmailSecondRequestStep);
                        }
                    }
                    onVerify={sendVerifyEmailRequest}
                    // Only trigger sending the verification email when this component is mounted and we are in the login flow
                    sendCodeOnMount={savedEmail.emailAddress !== emailAddressWithCodeSent.current && props.isForLogin}
                    onClose={props.onClose}
                    showCloseButton={props.showCloseButton}
                />
            );

        default:
            return null;
    }
}

export { RequiredRecoveryEmailController };
